import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import siteImages from './images/site';
import longevityImages from './images/longevity';
import { HOME_LOGO_SIZES, LONGEVITY_HERO_SIZES, CIVITAS_HERO_LOGO_SIZES } from './images/site-sizes.mjs';
import { COVERS as typesetterCovers } from './typesetter/vignettes';
import warmImages from './warmImages';
import ParticlesBackground from './ParticlesBackground';
import { ABOUT } from './home/content';
import './home/HomeCard.css';

// The game is a separate chunk so the home bundle stays as light as before.
// It is fetched once the page is idle, and the Other projects section only
// opens once the component is in hand, so the card never expands onto a
// placeholder and then resizes again when the real content arrives.
const loadGame = () => import('./home/Elementle');

// The hero images of the pages the tiles link to, fetched while the visitor
// looks at the home page so they are already cached on click-through.
const LINKED_PAGE_IMAGES = [
    { ...siteImages['civitas-globe'], sizes: CIVITAS_HERO_LOGO_SIZES },
    { ...longevityImages.daily, sizes: LONGEVITY_HERO_SIZES },
    ...typesetterCovers.map((src) => ({ src })),
];

// The three tiles are the page's only images, so they load at high priority
// (and scripts/preload-postbuild.js preloads them from the static HTML).
function HomeLogo({ image, alt }) {
    return (
        <img
            src={image.src}
            srcSet={image.srcSet}
            sizes={HOME_LOGO_SIZES}
            width={image.width}
            height={image.height}
            alt={alt}
            className="home-logo"
            fetchpriority="high"
            decoding="async"
        />
    );
}

function Chevron() {
    return (
        <svg className="hc-chevron" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3.5 6l4.5 4.5L12.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

// A card section that expands and collapses. The body animates height via a
// 0fr → 1fr grid track (see .hc-body in home/HomeCard.css). Content mounts in
// the same render that opens the section and stays mounted afterwards, so the
// game state survives collapsing.
// `expands` says which way the section visually grows (the card is centred,
// so About me at the top pushes upwards); the chevron points that way while
// closed and flips when open.
function ExpandableSection({ id, title, open, onToggle, expands = 'down', className = '', children }) {
    const [mounted, setMounted] = useState(open);
    if (open && !mounted) setMounted(true);
    return (
        <section className={`hc-section ${className}`.trim()}>
            <button
                type="button"
                className={`hc-head${open ? ' is-open' : ''}${expands === 'up' ? ' expands-up' : ''}`}
                aria-expanded={open}
                aria-controls={id}
                onClick={onToggle}
            >
                <span className="hc-title">{title}</span>
                <Chevron />
            </button>
            <div id={id} className={`hc-body${open ? ' is-open' : ''}`}>
                <div className="hc-body-inner" aria-hidden={!open}>
                    <div className="hc-body-content">{mounted ? children : null}</div>
                </div>
            </div>
        </section>
    );
}

function HomePage() {
    // One expandable section open at a time keeps the card a sensible size.
    const [openSection, setOpenSection] = useState(null);
    const flip = (name) => setOpenSection((cur) => (cur === name ? null : name));

    // The game component, once its chunk has loaded.
    const [Game, setGame] = useState(null);
    const gameLoad = useRef(null);
    const ensureGame = () => {
        if (!gameLoad.current) {
            gameLoad.current = loadGame()
                .then((mod) => { setGame(() => mod.default); })
                .catch(() => { gameLoad.current = null; });
        }
        return gameLoad.current;
    };

    const toggle = (name) => {
        if (name === 'projects' && !Game) {
            // setGame and setOpenSection batch into one render, so the section
            // opens with its content already in place.
            ensureGame().then(() => flip('projects'));
            return;
        }
        flip(name);
    };

    useEffect(() => {
        warmImages(LINKED_PAGE_IMAGES);
        // No point fetching the game where the section is hidden (phones).
        if (!window.matchMedia('(min-width: 601px)').matches) return undefined;
        const idle = window.requestIdleCallback
            ? window.requestIdleCallback(ensureGame, { timeout: 3000 })
            : window.setTimeout(ensureGame, 1500);
        return () => {
            if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
            else window.clearTimeout(idle);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
        {/* Must stay OUTSIDE the fixed container below: iOS Safari 26 composites
            only document-layer pixels behind its Liquid Glass toolbar, so a
            position:fixed / overflow:hidden ancestor keeps the ocean out of the
            glass and clips its under-toolbar overhang (see .particles-ocean in
            index.css). */}
        <ParticlesBackground />
        <div style={styles.container}>
            <div className={`blurred-card${openSection === 'projects' ? ' is-wide' : ''}`}>
                <ExpandableSection
                    id="hc-about"
                    title="About me"
                    expands="up"
                    open={openSection === 'about'}
                    onToggle={() => toggle('about')}
                >
                    <div className="hc-about">
                        {ABOUT.paragraphs.map((para) => (
                            typeof para === 'string' ? (
                                <p key={para}>{para}</p>
                            ) : (
                                <p key={para.wide}>
                                    <span className="hc-wide">{para.wide}</span>
                                    <span className="hc-narrow">{para.narrow}</span>
                                </p>
                            )
                        ))}
                        {ABOUT.links.length > 0 && (
                            <div className="hc-about-links">
                                {ABOUT.contactLead && <span className="hc-about-lead">{ABOUT.contactLead}</span>}
                                {ABOUT.links.map((l) => (
                                    <a key={l.href} href={l.href} target={l.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer">
                                        {l.label}
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                </ExpandableSection>

                <section className="hc-section">
                    <h2 className="hc-head"><span className="hc-title">Apps</span></h2>
                    <div className="hc-apps">
                        <Link to="/civitas" className="hc-app">
                            <HomeLogo image={siteImages['civitas-logo']} alt="Civitas Logo" />
                        </Link>
                        <Link to="/typesetter" className="hc-app">
                            <HomeLogo image={siteImages['typesetter-logo']} alt="Typesetter Logo" />
                        </Link>
                        {/* Zero Proof intentionally omitted from the home grid —
                            still reachable directly at /zeroproof. Buzzin likewise
                            lives only at /buzzin. */}
                        <Link to="/longevitychecklist" className="hc-app">
                            <HomeLogo image={siteImages['longevity-logo']} alt="Longevity Checklist Logo" />
                        </Link>
                    </div>
                </section>

                {/* Hidden on phones for now (see .hc-projects in HomeCard.css):
                    the game and the keyboard don't get along in the card yet. */}
                <ExpandableSection
                    id="hc-projects"
                    className="hc-projects"
                    title="Other projects"
                    open={openSection === 'projects'}
                    onToggle={() => toggle('projects')}
                >
                    {Game && <Game />}
                </ExpandableSection>
            </div>
        </div>
        </>
    );
}

const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
        // Click-through so taps reach the ocean canvas underneath (ripples);
        // .blurred-card re-enables pointer events for the links.
        pointerEvents: 'none',
    },
};

export default HomePage;
